// Tanstack query mutations for authentication (login, register, logout)

import type z from "zod";
import { jwtDecode } from "jwt-decode";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import type { loginSchema, registerSchema } from "./validators";
import { paths } from "@/config/paths";
import useCurrentUser from "@/store/useCurrentUser";
import { CLIENT_ROLES } from "@/types/user";
import { api } from "@/lib/api-client";
import { apiEndpoints } from "@/lib/api";
import type { User } from "@/types/user";
import { UserRole } from "@/types/user";

export type LoginData = z.infer<typeof loginSchema>;
export type SignupInput = z.infer<typeof registerSchema>;

const login = async (data: LoginData): Promise<{ token: string }> => {
  return api.post(apiEndpoints.auth.login, data);
};

const register = async (data: SignupInput): Promise<void> => {
  return api.post(apiEndpoints.auth.register, data);
};

export const useLogin = () => {
  const navigate = useNavigate();
  const setUser = useCurrentUser((state: any) => state.setUser);
  const setToken = useCurrentUser((state: any) => state.setToken);

  return useMutation({
    mutationFn: login,
    onSuccess: (data: { token: string }) => {
      setToken(data.token);
      const payload = jwtDecode(data.token) as Partial<User>;
      setUser(payload);
      toast.success("Sign in successful!");

      const targetPath = CLIENT_ROLES.includes(payload.role as UserRole)
        ? paths.app.portal.getHref()
        : paths.app.root.path;
      navigate(targetPath);
    },
    onError: () => {
      // Error is handled by api client interceptor
    },
  });
};

export const useRegister = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: register,
    onSuccess: () => {
      toast.info("Registration successful! Please log in.");
      navigate(paths.auth.login.path);
    },
    onError: () => {
      // Error is handled by api client interceptor
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const removeUser = useCurrentUser((state: any) => state.removeUser);

  return () => {
    removeUser();
    navigate(paths.auth.login.path);
    toast.info("You have been logged out.");
  };
};

export const useAssetTokenSync = () => {
  const isAuthenticated = useCurrentUser((state) => state.isAuthenticated());

  return useQuery({
    queryKey: ["asset-token"],
    enabled: isAuthenticated,

    queryFn: async () => {
      // use your existing api client rather than raw fetch — it should already
      // attach the session Authorization header and already have a 401 interceptor
      // that logs the user out. Don't duplicate that logic here.
      const response = await api.post("/auth/asset-token");
      useCurrentUser.getState().setAssetToken((response.data as any).assetToken);
      return (response.data as any).assetToken;
    },

    // refetches every 8 minutes since asset tokens are valid for 10 minutes. This ensures that the token is refreshed before it expires.
    staleTime: 8 * 60 * 1000,
    refetchInterval: 8 * 60 * 1000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: 3,
  });
};
